import { classnames } from 'Utils/classnames/classnames.utils';
import { camelCase } from 'lodash';
import { useRouter } from 'next/router';
import React, { memo } from 'react';
import { windowWrapper } from '@clubmed/utils';
import DynamicLinkUtil from 'Components/frameworks/dynamic-link/DynamicLinkUtil';
import { Button } from 'Components/frameworks/button/Button';
import type { RatioDisplay } from 'Components/frameworks/image/Image';
import {
  generateLinkProp,
  generateProductLink,
  generateCmtaLinkProp,
  localeHasCmtaLinks,
} from 'Components/widgets/product/Product.utils';
import type { ProductProps, ctmaLinksParams } from 'Components/widgets/product/Product.utils';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import type { ChildcareAgeInMonthsProps } from 'Components/widgets/product/Product.mapper';
import { ProductHeader } from 'Components/widgets/product/product-header/ProductHeader';
import { ProductThumbnail } from 'Components/widgets/product/product-thumbnail/ProductThumbnail';
import { ActivitiesProductCard } from 'Components/widgets/product/product-card/ActivitiesProductCard';
import { TripadvisorProductCard } from 'Components/widgets/product/product-card/TripadvisorProductCard';
type Props = ProductProps & {
  childcareAgeInMonths: ChildcareAgeInMonthsProps;
  cmtaLinks?: ctmaLinksParams;
  index?: number;
  isCompact?: boolean;
  lineName?: string;
  offer?: Offer;
  onComparisonClick?: () => unknown;
  ratioDisplay?: RatioDisplay;
};
export const ProductCard = memo<Props>(
  ({
    className,
    childcareAgeInMonths = [],
    cmtaLinks,
    comfort,
    flag = {},
    hasComparison,
    hasFadeIn = true,
    hasLazyLoading,
    header,
    id,
    index,
    isCompact = false,
    isLinkDisabled,
    lineName,
    offer,
    onClick,
    ratioDisplay,
    thumbnail,
    url,
  }: Props) => {
    const appContext = useAppContext();
    const router = useRouter();
    const track = useTracking({});
    const pageContext = usePageContext();
    const siteSection = pageContext?.siteSection || '';
    const clickThumbnailTrackingData =
      typeof index === 'number'
        ? {
            actionGA: 'clickThumbnail',
            labelGA: id,
            resortCode: id,
            resortTrident: typeof comfort === 'number' ? comfort : '',
            thumbnailPrice: flag?.price?.bestPrice?.value || '',
            thumbnailList: lineName ? `${siteSection}_${camelCase(lineName)}` : siteSection,
            thumbnailPosition: index + 1,
          }
        : null;
    const linkShouldBeDisabled = isLinkDisabled || hasComparison;

    const goOnResort = () => {
      const newHistoryState = new DynamicLinkUtil(
        windowWrapper.getLocation(),
        url ? generateProductLink(url, offer) : '',
        {},
      );
      router.push(newHistoryState.toString());
    };

    return (
      <article
        className={classnames(
          'relative isolate min-h-[414px] overflow-hidden rounded-12 bg-white',
          {
            'animation-duration-750 animate-fadeIn': hasFadeIn,
          },
          className,
        )}
        onClick={onClick}
      >
        <div
          className={classnames('relative isolate', {
            flex: isCompact,
          })}
        >
          <div
            data-testid="Product-Header-Wrapper"
            className={classnames('relative', {
              'w-1/2 flex-shrink-0': isCompact,
            })}
          >
            <ProductThumbnail
              {...thumbnail}
              topTags={[]}
              ratioDisplay={ratioDisplay}
              hasLazyLoading={hasLazyLoading}
              isLinkDisabled
              nextLabel={t('common.next')}
              previousLabel={t('common.previous')}
            />
          </div>
          <div className="flex flex-col px-15 py-20">
            <ProductHeader
              heading={header.heading}
              isLinkDisabled={linkShouldBeDisabled}
              link={
                appContext.isCmta && id && cmtaLinks && localeHasCmtaLinks(cmtaLinks)
                  ? generateCmtaLinkProp(id, cmtaLinks)
                  : generateLinkProp(url, offer)
              }
              onClick={() => {
                if (clickThumbnailTrackingData) {
                  track(clickThumbnailTrackingData);
                }
              }}
            />

            <TripadvisorProductCard productId={id} />
            <ActivitiesProductCard childcareAgeInMonths={childcareAgeInMonths} productId={id} />

            <Button className="mx-auto mt-20" onClick={goOnResort} size="small">
              {t('resort.pricePopin.buttonLabel')}
            </Button>
          </div>
        </div>
      </article>
    );
  },
);
